import React, { FC, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AuthenticationCard } from '@components/atoms/AuthenticationCard';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Space } from '@components/atoms/Space';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Typography } from '@components/atoms/Typography';
import { Form } from '@components/atoms/Form';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { REGEX_IS_EMAIL, SITE_URLS } from '@constants';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useData } from '@hooks/useData';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { Modules, useModule } from '@hooks/useModule';
import { useLogin } from '@services/useLogin';
import { useGoogleAuthService } from '@services/useGoogleAuthService';
import { useNotifications } from '@hooks/useNotifications';

export const LoginCard: FC = () => {
    const { t } = useTranslation();
    const { isModuleActive } = useModule();
    const { login } = useData();
    const { navigate } = useCustomNavigate();
    const { showDefaultNotification } = useNotifications();
    const { handleGoogleLogin, error: googleLoginError, lastResponseTime } = useGoogleAuthService();

    const translations = {
        title: t('login-card-component.title'),
        subtitle: t('login-card-component.subtitle'),
        or: t('login-card-component.or'),
        loginWithGoogleButton: t('login-card-component.login-with-google.button'),
        noAccount: t('login-card-component.no-account'),
        createAccountButton: t('login-card-component.create-account.button'),
    };

    const handleLoginSuccess = (response: CredentialResponse) => {
        handleGoogleLogin(response.credential ?? '');
    };

    useEffect(() => {
        if (googleLoginError) {
            showDefaultNotification({
                type: 'error',
                message: googleLoginError,
            });
        } else if (login) {
            navigate(`/${SITE_URLS.home}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime, googleLoginError]);

    return (
        <AuthenticationCard title={translations.title} subtitle={translations.subtitle}>
            <>
                <LoginForm />
                <Space spaceBreakpoints={{ xs: { space: 30 } }} />

                {isModuleActive(Modules.googleAuth) && (
                    <>
                        {/* login with google */}
                        <AuthenticationCard.Divider text={translations.or} />
                        <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                        <GoogleLogin text="continue_with" onSuccess={handleLoginSuccess} />
                        <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                    </>
                )}

                {/* create account */}
                <AuthenticationCard.Divider text={translations.noAccount} />
                <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                <Anchor href="register" mode={AnchorMode.primary}>
                    {translations.createAccountButton}
                </Anchor>
            </>
        </AuthenticationCard>
    );
};

interface LoginFormErrors {
    email: string[];
    password: string[];
}

const LoginForm: FC = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const redirectTo = params.get('redirectTo');
    const { navigate } = useCustomNavigate();
    const { t } = useTranslation();
    const { login } = useData();
    const { showDefaultNotification } = useNotifications();
    const { request: requestLogin, loading, error: errorLogin, lastResponseTime } = useLogin();

    const translations = {
        emailLabel: t('login-card-component.email.label'),
        passwordLabel: t('login-card-component.password.label'),
        resetPasswordLink: t('login-card-component.reset-password.link'),
        loginButton: t('login-card-component.login.button'),
        continueWithEmailButton: t('login-card-component.continue-with-email.button'),
        emailRequiredError: t('login-card-component.email.required-error'),
        emailInvalidError: t('login-card-component.email.invalid-error'),
        passwordRequiredError: t('login-card-component.password.required-error'),
        passwordIncorrectError: t('login-card-component.password.incorrect-error'),
    };

    const formRef = useRef<HTMLFormElement>(null);
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');

    const [continueWithEmail, setContinueWithEmail] = useState<boolean>(false);

    // errors to be displayed in the form
    const [errors, setErrors] = useState<LoginFormErrors>({
        email: [],
        password: [],
    });

    // if email is valid, request password
    const handleOnClickContinueWithEmail = () => {
        const errors = validateForm();
        if (errors.email.length === 0) {
            setContinueWithEmail(true);
            // reset errors
            setErrors({ email: [], password: [] });
        }
    };

    const handleOnClickLogin = () => {
        const errors = validateForm();

        if (errors.email.length === 0 && errors.password.length === 0) {
            formRef?.current?.requestSubmit();
        }
    };

    const validateForm: () => LoginFormErrors = () => {
        const newErrors: LoginFormErrors = {
            email: [],
            password: [],
        };

        // check if email is inserted
        if (!email.trim()) {
            newErrors.email.push(translations.emailRequiredError);
        } else {
            // check if email is valid
            !REGEX_IS_EMAIL.test(email.trim()) && newErrors.email.push(translations.emailInvalidError);
        }

        // password is required
        !password.trim() && newErrors.password.push(translations.passwordRequiredError);

        setErrors(newErrors);

        return newErrors;
    };

    const submitForm = () => requestLogin({ email, password });

    // if logged in successfully, go to homepage
    // if not, show error
    useEffect(() => {
        if (errorLogin) {
            showDefaultNotification({
                type: 'error',
                message: errorLogin,
            });
        } else if (login?.data?.redirect) {
            navigate(`/${SITE_URLS.twoFactorAuthVerify}`);
        } else if (login) {
            navigate(redirectTo ? redirectTo : `/${SITE_URLS.home}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime, errorLogin]);

    const handleOnEnterPress = () => (continueWithEmail ? handleOnClickLogin() : handleOnClickContinueWithEmail());

    return (
        <Form formRef={formRef} classes="d-flex flex-column" onEnterPress={handleOnEnterPress} onSubmit={submitForm}>
            <>
                {/* email */}
                <Input
                    type={InputTypes.email}
                    label={`${translations.emailLabel}*`}
                    variant={InputVariants.labeledInput}
                    value={email}
                    onChange={(value) => setEmail(value)}
                    state={errors.email.length > 0 ? InputStates.error : undefined}
                    errorMessages={errors.email}
                />

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                {/* password + forgot password */}
                {continueWithEmail ? (
                    <>
                        <Input
                            type={InputTypes.password}
                            label={`${translations.passwordLabel}*`}
                            variant={InputVariants.labeledInput}
                            value={password}
                            onChange={(value) => setPassword(value)}
                            state={errors.password.length > 0 ? InputStates.error : undefined}
                            errorMessages={errors.password}
                        />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        <Anchor
                            href={`${SITE_URLS.recoverPassword}?email=${email}`}
                            classes="d-block text-decoration-none"
                            mode={AnchorMode.none}>
                            <Typography fontSize="12" color="primary">
                                {translations.resetPasswordLink}
                            </Typography>
                        </Anchor>
                        <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                        <Button
                            label={translations.loginButton}
                            mode={ButtonMode.primary}
                            onClick={() => handleOnClickLogin()}
                            disabled={loading}
                        />
                    </>
                ) : (
                    <Button
                        type="button"
                        label={translations.continueWithEmailButton}
                        mode={ButtonMode.primary}
                        onClick={() => handleOnClickContinueWithEmail()}
                    />
                )}
            </>
        </Form>
    );
};